import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  LockKeyhole,
  Mail,
  Gift,
  Crown,
  Check,
  ArrowRight,
} from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';
import { Button } from 'src/components/ui/button';
import { isLockedAttribution } from '../Address/utils';

export interface LockedAttributionHoverCardProps {
  /** The attribution value to check and display */
  attributionValue: string | null | undefined;
  /** The type of attribution being locked */
  attributionType:
    | 'entity'
    | 'beneficial_owner'
    | 'custodian'
    | 'creator'
    | 'initiator';
  /** Custom trigger element - if not provided, will render just a lock icon */
  children?: React.ReactNode;
  /** Size variant for the lock icon */
  iconSize?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes for the trigger */
  triggerClassName?: string;
  /** Fallback content to render if not locked */
  fallback?: React.ReactNode;
  /** Callback handlers for actions */
  onSignUp?: () => void;
  onContactSales?: () => void;
}

// Constants
const HOVER_DELAYS = {
  open: 200,
  close: 100,
} as const;

const ATTRIBUTION_LABELS: Record<
  LockedAttributionHoverCardProps['attributionType'],
  string
> = {
  entity: 'Entity',
  beneficial_owner: 'Beneficial Owner',
  custodian: 'Custodian',
  creator: 'Creator',
  initiator: 'Initiator',
};

const SIZE_CLASSES = {
  container: {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  },
  icon: {
    sm: 'h-3 w-3',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  },
} as const;

const getLockType = (
  value: string | null | undefined,
): 'whitelist_locked' | 'premium_locked' | null => {
  if (value === 'whitelist_locked') return 'whitelist_locked';
  if (value === 'premium_locked') return 'premium_locked';
  return null;
};

export const LockedAttributionHoverCard: React.FC<
  LockedAttributionHoverCardProps
> = ({
  attributionValue,
  attributionType,
  children,
  iconSize = 'md',
  triggerClassName = '',
  fallback,
  onSignUp,
  onContactSales,
}) => {
  const navigate = useNavigate();

  const handleSignUp = onSignUp ?? (() => navigate('/register'));
  const handleContactSales =
    onContactSales ??
    (() => {
      window.open(
        'https://www.elementus.io/schedule',
        '_blank',
        'noopener,noreferrer',
      );
    });
  // If not locked, render fallback or nothing
  if (!isLockedAttribution(attributionValue)) {
    return <>{fallback}</>;
  }

  const lockType = getLockType(attributionValue)!;
  const isWhitelistLocked = lockType === 'whitelist_locked';
  const attributionLabel = ATTRIBUTION_LABELS[attributionType];
  const iconSizeClass = SIZE_CLASSES.icon[iconSize];
  const containerSizeClass = SIZE_CLASSES.container[iconSize];

  const renderDefaultTrigger = () => (
    <div className={triggerClassName}>
      <div
        className={`flex items-center justify-center rounded-md ${containerSizeClass} ${
          isWhitelistLocked
            ? 'bg-muted-100 text-muted-700'
            : 'bg-primary-50 text-primary-400'
        }`}
      >
        {isWhitelistLocked ? (
          <Lock className={iconSizeClass} />
        ) : (
          <LockKeyhole className={iconSizeClass} />
        )}
      </div>
    </div>
  );

  const renderHeaderIcon = () => (
    <div
      className={`flex h-12 w-12 items-center justify-center ${
        isWhitelistLocked
          ? 'rounded-full bg-primary-100'
          : 'rounded-full bg-amber-100'
      }`}
    >
      {isWhitelistLocked ? (
        <Gift className="h-6 w-6 text-primary-600" />
      ) : (
        <Crown className="h-6 w-6 text-amber-600" />
      )}
    </div>
  );

  const renderWhitelistContent = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        {renderHeaderIcon()}
        <div className="flex-1">
          <h3 className="mb-1 text-xl font-bold text-muted-900">
            Unlock Whitelisted {attributionLabel}
          </h3>
          <p className="text-sm font-medium text-primary-600">
            Get 100 Monthly Attributions for FREE!
          </p>
        </div>
      </div>

      {/* Description */}
      <p className="leading-relaxed text-muted-700">
        We&apos;ve found valuable {attributionLabel.toLowerCase()} data for you.
        Create a free account to view it instantly.
      </p>

      {/* Benefits Section */}
      <div className="rounded-r-lg border-l-4 border-primary-500 bg-primary-50 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary-600" />
          <h4 className="font-semibold text-primary-900">
            Get Started for Free:
          </h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-muted-700">
              Access detailed {attributionLabel.toLowerCase()} information
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-muted-700">
              Claim 100 monthly whitelisted attributions
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-muted-700">No credit card required</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={handleSignUp}
          className="flex-1 rounded-lg bg-primary-600 py-3 font-medium text-white transition-colors hover:bg-primary-700"
        >
          <span>Sign Up Free & View Data</span>
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Footer */}
      {/* <div className="flex items-center justify-center pt-2">
        <button
          onClick={onFaqClick}
          className="flex items-center gap-2 text-sm text-muted-500 transition-colors hover:text-muted-700"
        >
          <HelpCircle className="h-4 w-4" />
          <span>Learn More</span>
        </button>
      </div> */}
    </div>
  );

  const renderPremiumContent = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        {renderHeaderIcon()}
        <div className="flex-1">
          <h3 className="mb-1 text-xl font-bold text-muted-900">
            Access Exclusive Premium Data
          </h3>
          <p className="text-sm font-medium text-amber-600">
            Unlock Top-Tier Enterprise Insights
          </p>
        </div>
      </div>

      {/* Description */}
      <p className="leading-relaxed text-muted-700">
        This powerful {attributionLabel.toLowerCase()} data is reserved for our
        enterprise partners, offering unparalleled depth and features.
      </p>

      {/* Benefits Section */}
      <div className="rounded-r-lg border-l-4 border-amber-500 bg-amber-50 p-4">
        <div className="mb-3 flex items-center gap-2">
          <Crown className="h-5 w-5 text-amber-600" />
          <h4 className="font-semibold text-amber-900">
            Benefits of Premium Access:
          </h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-muted-700">
              Comprehensive data sets & analytics
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-muted-700">Dedicated enterprise support</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-muted-700">
              Customizable solutions & integrations
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={handleContactSales}
          className="flex-1 rounded-lg bg-amber-600 py-3 font-medium text-white transition-colors hover:bg-amber-700"
        >
          <Mail className="mr-2 h-4 w-4" />
          <span>Contact Sales for Full Access</span>
        </Button>
      </div>

      {/* Footer */}
      {/* <div className="flex items-center justify-center pt-2">
        <button
          onClick={onFaqClick}
          className="flex items-center gap-2 text-sm text-muted-500 transition-colors hover:text-muted-700"
        >
          <HelpCircle className="h-4 w-4" />
          <span>Premium Details</span>
        </button>
      </div> */}
    </div>
  );

  return (
    <HoverCard openDelay={HOVER_DELAYS.open} closeDelay={HOVER_DELAYS.close}>
      <HoverCardTrigger asChild>
        {children ?? renderDefaultTrigger()}
      </HoverCardTrigger>
      <HoverCardContent
        className="w-[420px] border-0 p-0 shadow-xl"
        side="bottom"
        align="start"
      >
        <div className="rounded-xl bg-white p-6 shadow-2xl">
          {isWhitelistLocked
            ? renderWhitelistContent()
            : renderPremiumContent()}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
